import React from 'react';
import { SurveysAPI } from '../../API';
import { callbackUserId, decideCallbackOutcome, displayFor, endLinkQuery, INVALID_LINK_MESSAGE, isServerRecordedView, linkUserId } from './callbackOutcome';

// Static-callback thank-you pages (brand-neutral DX surface).
//
// The client redirects the respondent's browser to ONE of these fixed paths:
//
//   /#/<completed|terminate|overquota|qualityterminate>?userid=<respondent id>
//
// (The earlier /#/thank-you/<status> paths route here too.) Unlike the dynamic
// links, static links carry no surveyid.
//
// The page renders ONLY from the backend's answer, never from the path. It
// POSTs { userId, status } WITHOUT a surveyId to GetUserOneAssignedSurveyCallback
// (which resolves the survey from the respondent's most recent started entry)
// and then shows one of:
//   - "Recording..." until the backend has answered (no status is visible yet)
//   - a redirect, when the backend returns an onward URL (partner passback,
//     the panelist's country thank-you page, or the partner hold page)
//   - the thank-you page, when the hit was recorded, or was already recorded
//     (then with the STORED status, so a genuine refresh looks right)
//   - "This link is not valid or has expired." for everything else: a
//     made-up id, never entered, a mismatched or closed survey, an error, no
//     response. Reasons stay in the console only.
// A missing or placeholder userid ("{userid}" the client never substituted)
// is still POSTed, as userid '' with placeholderUserId=true; the backend
// identifies the respondent by the entry from this IP, or rejects the hit.
// See callbackOutcome.ts for the rule.
//
// /#/not-recorded never records.
//
// ?dxview=1 (SCRUM-130): the admin backend's GET /r end link has already
// recorded the hit and sent the respondent here with the STORED status in the
// path. The page only displays it and never posts.
//
// Public route, mounted outside the admin auth wrapper (see App.tsx).

export type ThankYouStatus = 'completed' | 'terminate' | 'overquota' | 'qualityterminate' | 'not-recorded';

const NOT_RECORDED = {
    title: 'We could not confirm your response',
    body: 'The survey provider was unable to record your result with us, so it has not been saved. '
        + 'Please contact the survey provider or your panel support team and quote the reference time below.',
};

type OutcomeStatus = Exclude<ThankYouStatus, 'not-recorded'>;

// The status each path REPORTS to the backend. It is never displayed: what
// the page shows comes from the backend's answer.
const OUTCOME_DISPLAY: Record<OutcomeStatus, { backendStatus: string }> = {
    completed:        { backendStatus: 'Completed' },
    terminate:        { backendStatus: 'Terminated' },
    overquota:        { backendStatus: 'Over Quota' },
    qualityterminate: { backendStatus: 'Quality Terminated' },
};

// HashRouter keeps the query string inside the hash (#/completed?userid=Y);
// read it from there, as SurveyCallbackPage does. Params a provider appended
// before the '#' have already been folded in by normalizeTrackingParams() in
// index.tsx. Recontact links: a UUID newids is our id and wins over userid.
const readHashParams = () => {
    const hash = window.location.hash || '';
    const queryStart = hash.indexOf('?');
    return new URLSearchParams(queryStart === -1 ? '' : hash.slice(queryStart + 1));
};
const readUserId = () => callbackUserId(linkUserId(readHashParams()));

const cellStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'center',
    border: '1px solid #dee2e6',
    wordBreak: 'break-all',
};

const headStyle: React.CSSProperties = {
    padding: '12px',
    textAlign: 'center',
    fontWeight: 600,
    border: '1px solid #212529',
};

interface Props {
    status: ThankYouStatus;
}

type View =
    | { kind: 'loading' }
    | { kind: 'invalid' }
    | { kind: 'thanks'; status: string };

const SurveyThankYouPage: React.FC<Props> = ({ status }) => {
    // Reference time for support (not-recorded page only): lets us match the
    // respondent's report to the callback_failures row without putting any
    // identifier in the URL.
    const [referenceTime] = React.useState(() => new Date().toISOString());
    const outcome = status !== 'not-recorded' ? OUTCOME_DISPLAY[status] : undefined;
    const [userIdPart] = React.useState(readUserId);
    const userId = userIdPart.userId;
    // An outcome page starts in 'loading' and never shows a status until the
    // backend has answered.
    const [serverRecorded] = React.useState(() => isServerRecordedView(readHashParams()));
    const [view, setView] = React.useState<View>(() => (
        !outcome ? { kind: 'invalid' }
            : serverRecorded ? { kind: 'thanks', status: outcome.backendStatus }
                : { kind: 'loading' }
    ));

    React.useEffect(() => {
        if (!outcome || serverRecorded) return;
        if (userIdPart.placeholderUserId) console.warn('Callback page: no usable userid in the link; the backend matches by IP');
        SurveysAPI.GetUserOneAssignedSurveyCallback({
            ...userIdPart,
            partnerId: 'NA',
            status: outcome.backendStatus,
            endLinkQuery: endLinkQuery(readHashParams()),
        })
            .then((result: any) => {
                const decision = decideCallbackOutcome(result, outcome.backendStatus);
                if (decision.kind === 'redirect') {
                    // replace(): Back must not return here and re-post the hit.
                    // Stay on "Recording..." while the browser leaves.
                    window.location.replace(decision.url);
                    return;
                }
                if (decision.kind === 'invalid') {
                    console.warn('Callback not recorded:', decision.reason);
                    setView({ kind: 'invalid' });
                    return;
                }
                setView({ kind: 'thanks', status: decision.status });
            })
            .catch((err: any) => {
                console.error('Callback request failed:', err);
                setView({ kind: 'invalid' });
            });
        // Record once per page load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shell = (children: React.ReactNode) => (
        <div
            style={{
                minHeight: '100vh',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
            }}
        >
            <div style={{ width: '100%', maxWidth: '640px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
                <div style={{ marginBottom: '32px' }}>
                    <img src="/DX Logo.avif" alt="DataXing" style={{ width: '140px', height: 'auto' }} />
                </div>
                {children}
            </div>
        </div>
    );

    const neutralCard = (
        <div style={{ border: '1px solid #dee2e6', background: '#fafafa', color: '#212529', borderRadius: '4px', padding: '24px 20px' }}>
            <p style={{ marginBottom: 0 }}>{INVALID_LINK_MESSAGE}</p>
        </div>
    );

    if (view.kind === 'loading') {
        return shell(<div style={{ color: '#555', fontSize: '1rem' }}>Recording…</div>);
    }

    if (view.kind === 'invalid') {
        // The explicit /not-recorded page keeps its own message and reference time.
        if (status === 'not-recorded') {
            const c = NOT_RECORDED;
            return shell(
                <div style={{ border: '1px solid #f5c2c7', background: '#f8d7da', color: '#842029', borderRadius: '4px', padding: '24px 20px' }}>
                    <h4 style={{ marginBottom: '12px' }}>{c.title}</h4>
                    <p style={{ marginBottom: '16px' }}>{c.body}</p>
                    <p style={{ marginBottom: 0, fontSize: '0.9rem' }}>Reference time: <code>{referenceTime}</code></p>
                </div>
            );
        }
        return shell(neutralCard);
    }

    // Thank-you page: the status is the one the backend reported.
    const shown = displayFor(view.status);
    if (!shown) {
        return shell(neutralCard);
    }
    return shell(
        <>
            <div style={{ border: '1px solid #dee2e6', background: '#fafafa', color: '#212529', borderRadius: '4px', padding: '24px 20px' }}>
                <h4 style={{ marginBottom: '12px' }}>Thank you</h4>
                <p style={{ marginBottom: 0 }}>{shown.thanks}</p>
            </div>
            <table
                style={{
                    width: '100%',
                    marginTop: '24px',
                    borderCollapse: 'collapse',
                    fontSize: '0.95rem',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                }}
            >
                <thead>
                    <tr style={{ background: '#212529', color: '#ffffff' }}>
                        <th style={headStyle}>Respondent ID</th>
                        <th style={headStyle}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr style={{ background: '#fafafa' }}>
                        <td style={cellStyle}>{userId || '—'}</td>
                        <td style={{ ...cellStyle, wordBreak: 'normal' }}>
                            <span
                                style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    background: shown.bg,
                                    color: shown.fg,
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {shown.title}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
            <p style={{ marginTop: '24px', color: '#6c757d', fontSize: '0.9rem' }}>You can now close this window.</p>
        </>
    );
};

export default SurveyThankYouPage;
